import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { removeStorageFile } from '@/lib/storage';
import { canWrite, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

export interface Document {
  id: string;
  documentType: string;
  relatedEntityType: string;
  relatedEntityId: string;
  fileUrl: string;
  createdAt: string;
}

export interface DocumentsResult {
  data: Document[];
}

export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  order_confirmation: 'Confirmación de pedido',
  payment_receipt: 'Recibo de pago',
  sale_receipt: 'Recibo de venta',
  inventory_report: 'Reporte de inventario',
  financial_report: 'Reporte financiero',
  consignment_settlement: 'Liquidación de consignación',
  donation_receipt: 'Recibo de donación',
  repair_order: 'Orden de reparación',
};

export const ENTITY_TYPE_LABELS: Record<string, string> = {
  order: 'Pedido',
  sale: 'Venta',
  payment: 'Pago',
  consignment: 'Consignación',
  donation: 'Donación',
  warranty: 'Garantía',
};

export async function fetchDocuments(): Promise<DocumentsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const { data, error } = await supabase.from('documents').select('*');

    if (error) {
      throw new Error(error.message);
    }

    const mapped: Document[] = ((data ?? []) as Array<Record<string, any>>)
      .map((r) => ({
        id: r.id,
        documentType: r.document_type,
        relatedEntityType: r.related_entity_type,
        relatedEntityId: r.related_entity_id,
        fileUrl: r.file_url,
        createdAt: r.created_at ?? '',
      }))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

    return { data: mapped };
  } catch (err) {
    console.error('[documents] connection failed:', err);
    throw err;
  }
}

export interface DocumentInput {
  documentType: string;
  relatedEntityType: string;
  relatedEntityId: string;
  fileUrl: string;
}

export async function saveDocument(
  input: DocumentInput,
): Promise<{ data: Document | null; error: string | null }> {
  if (!(await canWrite('documents'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    document_type: input.documentType,
    related_entity_type: input.relatedEntityType,
    related_entity_id: input.relatedEntityId || null,
    file_url: input.fileUrl.trim(),
  };

  try {
    const { data, error } = await supabase.from('documents').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer el documento guardado.' };
    recordAudit({
      entityType: 'documents',
      entityId: row.id,
      action: 'CREATE',
      newValue: input.documentType,
    });
    return {
      data: {
        id: row.id,
        documentType: row.document_type,
        relatedEntityType: row.related_entity_type,
        relatedEntityId: row.related_entity_id,
        fileUrl: row.file_url,
        createdAt: row.created_at ?? '',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[documents] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar el documento.' };
  }
}

// Sube un archivo al bucket público `documents` y devuelve la URL pública lista
// para guardar en el campo `file_url` del documento. El nombre se sanitiza
// (minúsculas, sin espacios ni caracteres raros) y se antepone un timestamp
// para evitar colisiones.
export async function uploadDocumentFile(
  file: File,
): Promise<{ url: string | null; error: string | null }> {
  if (!isSupabaseConfigured || !supabase) {
    return { url: null, error: 'Supabase no está configurado. Conéctalo para subir archivos.' };
  }

  const safeBase =
    file.name
      .replace(/\.[^.]+$/, '')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'archivo';

  const ext = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'pdf';
  const path = `${Date.now()}-${safeBase}.${ext}`;

  try {
    const { error } = await supabase.storage
      .from('documents')
      .upload(path, file, { cacheControl: '3600', upsert: false });

    if (error) return { url: null, error: error.message };

    const { data } = supabase.storage.from('documents').getPublicUrl(path);
    return { url: data.publicUrl, error: null };
  } catch (err) {
    console.warn('[documents] upload file failed:', err);
    return { url: null, error: 'Error de conexión al subir el archivo.' };
  }
}

// Borra un archivo del bucket `documents` a partir de su URL pública. Si la URL
// es externa (no de Storage propio), no hace nada.
export async function deleteDocumentFile(url: string): Promise<{ error: string | null }> {
  return removeStorageFile('documents', url);
}